import React, { useState, useEffect } from "react";
import "./GuideBookingSummary.css";
import { FaUser, FaMapMarkerAlt, FaMoneyBillWave, FaCalendar, FaClock, FaCheckCircle, FaTimes, FaEye, FaCog } from "react-icons/fa";
import travellerImg from '../assets/traveller.png';
import NavUser from "../components/NavUser";

function GuideBookingSummary() {
  const [bookings, setBookings] = useState([]);
  const [todaysBookings, setTodaysBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    confirmedBookings: 0,
    totalEarnings: 0,
    todaysBookings: 0
  });

  useEffect(() => {
    const mockBookings = [
      {
        id: 1,
        traveler: {
          full_name: "Sarah Johnson",
          email: "sarah.j@email.com",
          phone_number: "+1-555-0123",
          Country: { name: "USA" }
        },
        tour_listing: {
          title: "Historic City Walking Tour",
          price: 150,
          currency: "USD"
        },
        booking_date: "2024-01-15",
        booking_time: "09:00",
        booking_status: { name: "Confirmed" },
        payment_status: { name: "Paid" },
        special_requests: "Please provide vegetarian lunch options"
      },
      {
        id: 2,
        traveler: {
          full_name: "Michael Chen",
          email: "m.chen@email.com",
          phone_number: "+1-555-0456",
          Country: { name: "Canada" }
        },
        tour_listing: {
          title: "Mountain Hiking Adventure",
          price: 200,
          currency: "USD"
        },
        booking_date: "2024-01-15",
        booking_time: "14:00",
        booking_status: { name: "Pending" },
        payment_status: { name: "Pending" },
        special_requests: "Need hiking equipment rental"
      },
      {
        id: 3,
        traveler: {
          full_name: "Emma Wilson",
          email: "emma.w@email.com",
          phone_number: "+1-555-0789",
          Country: { name: "UK" }
        },
        tour_listing: {
          title: "Cultural Heritage Tour",
          price: 120,
          currency: "USD"
        },
        booking_date: "2024-01-16",
        booking_time: "10:30",
        booking_status: { name: "Confirmed" },
        payment_status: { name: "Paid" },
        special_requests: "Wheelchair accessible tour needed"
      }
    ];

    setBookings(mockBookings);
    const today = new Date().toISOString().split('T')[0];
    const todays = mockBookings.filter(booking => booking.booking_date === today);
    setTodaysBookings(todays);

    const totalBookings = mockBookings.length;
    const confirmedBookings = mockBookings.filter(b => b.booking_status.name === 'Confirmed').length;
    const totalEarnings = mockBookings
      .filter(b => b.booking_status.name === 'Confirmed')
      .reduce((sum, b) => sum + b.tour_listing.price, 0);

    setStats({
      totalBookings,
      confirmedBookings,
      totalEarnings,
      todaysBookings: todays.length
    });
    setIsLoading(false);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  if (isLoading) {
    return (
      <div className="agent-page-premium">
        <NavUser />
        <div className="loader-container">
          <span className="loader-dots">Loading Analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-page-premium">
      <NavUser />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Guide Dashboard</h1>
          <p>Track your success, manage bookings, and grow your travel business.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        {/* Stats Section */}
        <div className="stats-dashboard-grid">
          <div className="premium-glass-card stat-mini">
            <div className="stat-icon-box blue"><FaCalendar /></div>
            <div className="stat-info">
              <span className="stat-label">Total Bookings</span>
              <span className="stat-number">{stats.totalBookings}</span>
            </div>
          </div>
          <div className="premium-glass-card stat-mini">
            <div className="stat-icon-box green"><FaCheckCircle /></div>
            <div className="stat-info">
              <span className="stat-label">Confirmed</span>
              <span className="stat-number">{stats.confirmedBookings}</span>
            </div>
          </div>
          <div className="premium-glass-card stat-mini">
            <div className="stat-icon-box purple"><FaMoneyBillWave /></div>
            <div className="stat-info">
              <span className="stat-label">Total Earnings</span>
              <span className="stat-number">${stats.totalEarnings}</span>
            </div>
          </div>
          <div className="premium-glass-card stat-mini">
            <div className="stat-icon-box orange"><FaClock /></div>
            <div className="stat-info">
              <span className="stat-label">Today</span>
              <span className="stat-number">{stats.todaysBookings}</span>
            </div>
          </div>
        </div>

        {/* Bookings Section */}
        <div className="dashboard-content-grid">
          <div className="dashboard-section-header">
            <h2>Active Bookings</h2>
            <div className="header-actions">
              <button className="btn-minimal"><FaCog /> Settings</button>
            </div>
          </div>

          <div className="premium-bookings-grid">
            {bookings.length === 0 ? (
              <div className="premium-glass-card empty-state-card">
                <FaCalendar className="empty-icon" />
                <p>No Bookings Found</p>
                <span>Your tour bookings will appear here when travelers book your services.</span>
              </div>
            ) : (
              bookings.map((booking) => (
                <div key={booking.id} className="premium-booking-card-v2">
                  <div className="card-top">
                    <div className="traveler-v2">
                      <img src={travellerImg} alt="Traveler" />
                      <div className="name-box">
                        <h4>{booking.traveler.full_name}</h4>
                        <span>{booking.traveler.Country.name}</span>
                      </div>
                    </div>
                    <div className="status-v2" style={{ color: getStatusColor(booking.booking_status.name), backgroundColor: `${getStatusColor(booking.booking_status.name)}15` }}>
                      {booking.booking_status.name}
                    </div>
                  </div>

                  <div className="card-mid">
                    <h5 className="tour-title">{booking.tour_listing.title}</h5>
                    <div className="meta-grid">
                      <div className="meta-item">
                        <FaCalendar /> <span>{formatDate(booking.booking_date)}</span>
                      </div>
                      <div className="meta-item">
                        <FaClock /> <span>{booking.booking_time}</span>
                      </div>
                      <div className="meta-item">
                        <FaMoneyBillWave /> <span>${booking.tour_listing.price} {booking.tour_listing.currency}</span>
                      </div>
                    </div>
                  </div>

                  {booking.special_requests && (
                    <div className="card-note">
                      <strong>Note:</strong> {booking.special_requests}
                    </div>
                  )}

                  <div className="card-actions-v2">
                    <button className="btn-v2-secondary"><FaEye /> Details</button>
                    <button className="btn-v2-primary">Manage</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


export default GuideBookingSummary;
